/**
 * Combat.
 *
 * None of this exists in v1 — grep `src/client` for health, damage, hitbox,
 * knockback or projectile and you get zero hits, and remote players are never
 * inserted into collision at all. Player-vs-player contact is new construction.
 *
 * Every number that decides an outcome is read from `@chaos/protocol`'s
 * CHARACTERS table on the server. The client's copy exists to size a telegraph
 * decal and draw a cooldown ring; it is never asked what an ability did.
 *
 * Shape of a cast:
 *
 *   press -> cooldown + resource checked -> committed (`busy`, aim frozen)
 *         -> wind-up elapses -> shape resolved against live targets -> recovery
 *
 * AIM is captured at commit and never re-read, for every ability: a spun
 * camera must not swing an in-flight cast around. ORIGIN depends on
 * `abilityAnchor` from @chaos/protocol — the same predicate the client's
 * telegraphs and VFX consult, so where the hit resolves and where it is drawn
 * can never drift apart:
 *
 *   world — the effect is a place, fixed at cast (Fault Line, ground zones,
 *           projectiles). Resolves from the FROZEN cast origin: the 600ms on
 *           Fault Line is dodgeable precisely because Monolith cannot drag the
 *           crack off the drawn line by sprinting through his own wind-up.
 *   body  — the effect emanates from the caster (melee cones, spheres, self,
 *           Blink Strike). Resolves from the LIVE body at resolve tick: a
 *           90ms swing frozen at the cast position detaches and hangs in the
 *           air behind a walking caster.
 */
import { abilityAnchor, getCharacter, isBurst, pelletOffsetRad, coinDamageMultiplier, } from "@chaos/protocol";
import { BARRAGE_SHELL_SPLASH, DASH_ABILITY_ID, DASH_COOLDOWN_MS, DASH_DURATION_MS, DECOY_ID_PREFIX, DECOY_MIN_SPEED, DT, GROUND_NONE, GROUND_ZONE_OFFSET, KILL_CREDIT_TICKS, KILL_HEAL, KILL_ULT_CHARGE, KNOCKBACK_LIFT_FRACTION, KNOCKBACK_SPEED_PER_UNIT, MELEE_VERTICAL_REACH, PROJECTILE_CONTACT_RADIUS, PROJECTILE_LAUNCH_COS, PROJECTILE_LAUNCH_SIN, PROJECTILE_MUZZLE_HEIGHT, PROJECTILE_SPAWN_OFFSET, PROJECTILE_WALL_STEP, PULL_DURATION_MS, TRAP_ARM_TICKS, ULT_CHARGE_PER_DAMAGE_DEALT, ULT_CHARGE_PER_DAMAGE_TAKEN, ULT_FULL, ZONE_HIT_EVENT_INTERVAL_TICKS, } from "./constants.js";
import { bodyHalfHeight, bodyRadius, chaosPlayerScale, findPlayer, maxHealth } from "./body.js";
// Moved to body.ts so loot.ts can share them; re-exported so callers are unchanged.
export { findPlayer, maxHealth } from "./body.js";
import { applyEffect, damageReductionPct, hasEffect, isInvisible } from "./effects.js";
import { onArenaFloor } from "./map.js";
import { clamp, dcos, dsin, msToTicks, PI, ticksToMs } from "./math.js";
import { nextPointInDisc } from "./rng.js";
import { endTravel, isTravelling, lungeClearance, startTravel, travelTicks } from "./travel.js";
import { sweepPoint } from "./collision.js";
import { dist2, dist2XZ, dist3, forwardFromYaw, pointRectDist2XZ, pointSegmentDist2XZ, rightFromYaw } from "./vec.js";
import { ZONE_SOURCE_ID } from "./zone.js";
import { CRATE_HALF_HEIGHT, CRATE_RADIUS, damageCrate, damageCratesWhere, isGhost, } from "./loot.js";
// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------
export function getAux(world, id) {
    const existing = world.aux[id];
    if (existing)
        return existing;
    const created = createAux();
    world.aux[id] = created;
    return created;
}
export function createAux() {
    return {
        coyoteTicks: 0,
        jumpBufferTicks: 0,
        jumpHeld: false,
        isJumping: false,
        castBufferSlot: null,
        castBufferTicks: 0,
        prevHealth: 0,
        lastHitById: null,
        lastHitAbilityId: "",
        lastHitTick: -1,
        lastZoneTick: -1,
        struck: [],
        travelAbilityId: "",
        standingIndex: GROUND_NONE,
        wasDashing: false,
        dashBufferTicks: 0,
        wasTravelling: false,
        wasFlight: false,
    };
}
export function abilityForSlot(characterId, slot) {
    return getCharacter(characterId).abilities[slot];
}
// ---------------------------------------------------------------------------
// Damage
// ---------------------------------------------------------------------------
/**
 * The one place health goes down.
 *
 * Returns the damage actually applied, which is what chain falloff and the
 * `hit` event both need. Returns 0 for anything the target was immune to, so a
 * caller can tell "hit for nothing" from "did not hit".
 */
/** Practice spawn/load shield: no damage, no push, no pull, no void. */
export function casualProtected(world, p) {
    return world.mode === "casual" && world.tick < (p.casualProtectionUntil ?? 0);
}
export function applyDamage(world, target, raw, attackerId, abilityId, events, report = true) {
    if (target.phase !== "alive")
        return 0;
    if (casualProtected(world, target))
        return 0;
    // The drop phase is invulnerable, so landing on someone's head is not a
    // strategy and spawn-camping a 5s flyover is not possible.
    if (world.tick <= world.dropEndTick)
        return 0;
    if (hasEffect(target, "iframe"))
        return 0;
    if (raw <= 0)
        return 0;
    // The attacker's coins, looked up once and reused for the credit below.
    // Zone, surface and chaos damage have no player behind them and no bonus.
    const attacker = attackerId !== target.id && attackerId !== ZONE_SOURCE_ID ? findPlayer(world, attackerId) : null;
    const dealt = attacker ? raw * coinDamageMultiplier(attacker.coins) : raw;
    const dmg = dealt * (1 - damageReductionPct(target) / 100);
    if (dmg <= 0)
        return 0;
    target.health -= dmg;
    const aux = getAux(world, target.id);
    // The zone is tracked separately rather than overwriting the last attacker.
    // Otherwise chasing someone to 5 HP and watching the storm finish them costs
    // you the kill AND the bounty, which taxes exactly the aggression the whole
    // health economy is built to reward.
    if (attackerId === ZONE_SOURCE_ID) {
        aux.lastZoneTick = world.tick;
    }
    else {
        aux.lastHitById = attackerId;
        aux.lastHitAbilityId = abilityId;
        aux.lastHitTick = world.tick;
    }
    // Ult charges from damage dealt and damage taken, and from nothing else.
    // Time alive was considered and rejected: it pays players to hide, which in a
    // winner-take-all royale is already the dominant strategy without help.
    target.ultCharge = Math.min(ULT_FULL, target.ultCharge + dmg * ULT_CHARGE_PER_DAMAGE_TAKEN);
    if (attacker) {
        attacker.damageDealt += dmg;
        attacker.ultCharge = Math.min(ULT_FULL, attacker.ultCharge + dmg * ULT_CHARGE_PER_DAMAGE_DEALT);
    }
    if (report) {
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId,
            targetId: target.id,
            abilityId,
            damage: dmg,
            x: target.x,
            y: target.y,
            z: target.z,
        });
    }
    return dmg;
}
/** Awards the kill bounty. The only way HP comes back in this game. */
export function awardKill(world, killer, abilityId) {
    killer.kills += 1;
    killer.health = Math.min(maxHealth(killer), killer.health + KILL_HEAL);
    killer.ultCharge = Math.min(ULT_FULL, killer.ultCharge + KILL_ULT_CHARGE);
    refundCooldownOnKill(world, killer, abilityId);
}
/** Volt's Blink Strike returns half its cooldown when it finishes someone. */
function refundCooldownOnKill(world, killer, abilityId) {
    const character = getCharacter(killer.characterId);
    for (const slot of SLOTS) {
        const ability = character.abilities[slot];
        if (ability.id !== abilityId)
            continue;
        for (const e of ability.effects ?? []) {
            if (e.kind !== "refund_cooldown_on_kill")
                continue;
            const readyTick = killer.cooldowns[abilityId] ?? 0;
            const remaining = readyTick - world.tick;
            if (remaining <= 0)
                return;
            killer.cooldowns[abilityId] = world.tick + Math.floor((remaining * (100 - e.pct)) / 100);
        }
        return;
    }
}
const SLOTS = ["basic", "skill1", "skill2", "ultimate"];
// ---------------------------------------------------------------------------
// Casting
// ---------------------------------------------------------------------------
export function abilityReady(world, p, ability, slot) {
    if (world.tick < (p.cooldowns[ability.id] ?? 0))
        return false;
    // An empty magazine is the basic attack's real gate; the 200ms fire
    // cooldown only stops a held button dumping every pip inside one wind-up.
    if (ability.ammo && p.ammo < 1)
        return false;
    if (slot === "ultimate") {
        if (p.ultCharge < ULT_FULL)
            return false;
        if (hasEffect(p, "suppress_abilities"))
            return false;
    }
    return true;
}
/**
 * The magazine, refilled. One pip per `reloadMs`, counted in whole ticks, and
 * the count runs whenever the magazine is short — firing does not pause it.
 * Both numbers are integers on the player, so the server and every predicting
 * client agree to the tick about when the next pip lands.
 */
export function stepReload(p) {
    const basic = getCharacter(p.characterId).abilities.basic;
    const ammo = basic.ammo;
    if (!ammo || p.ammo >= ammo.max) {
        p.reloadTicks = 0;
        return;
    }
    p.reloadTicks += 1;
    if (p.reloadTicks >= msToTicks(ammo.reloadMs)) {
        p.reloadTicks = 0;
        p.ammo += 1;
    }
}
/** Commits a player to a cast. Returns the event, or null if it could not start. */
export function startCast(world, p, slot, events) {
    const ability = abilityForSlot(p.characterId, slot);
    if (!abilityReady(world, p, ability, slot))
        return false;
    if (slot === "ultimate")
        p.ultCharge = 0;
    if (ability.ammo)
        p.ammo -= 1;
    if (ability.cooldownMs > 0)
        p.cooldowns[ability.id] = world.tick + msToTicks(ability.cooldownMs);
    const committedTicks = msToTicks(ability.windupMs + ability.recoveryMs);
    if (committedTicks > 0) {
        applyEffect(p, "busy", ability.windupMs + ability.recoveryMs, world.tick, p.id);
    }
    const resolveTick = world.tick + msToTicks(ability.windupMs);
    const cast = {
        playerId: p.id,
        abilityId: ability.id,
        slot,
        resolveTick,
        yaw: p.yaw,
        x: p.x,
        y: p.y,
        z: p.z,
        pellet: -1,
        stage: "strike",
    };
    const hit = ability.hit;
    if (hit.kind === "shot" && isBurst(hit)) {
        // A burst is one press, N resolves: a ladder of pending casts on the
        // frozen yaw. Recovery is authored to cover the ladder, so the caster is
        // `busy` for the whole burst and cannot start another until it is done.
        const interval = msToTicks(hit.burstIntervalMs);
        for (let i = 0; i < hit.pellets; i++) {
            world.casts.push({ ...cast, resolveTick: resolveTick + i * interval, pellet: i });
        }
    }
    else {
        world.casts.push(cast);
    }
    events.push({
        t: "ability_cast",
        tick: world.tick,
        playerId: p.id,
        abilityId: ability.id,
        x: p.x,
        z: p.z,
        yaw: p.yaw,
    });
    return true;
}
/** True while this player has a committed cast whose wind-up has not resolved. */
export function hasPendingCast(world, playerId) {
    for (const c of world.casts)
        if (c.playerId === playerId)
            return true;
    return false;
}
/** The universal dash: one charge, 6s, and the only i-frames in the game. */
export function tryDash(world, p, moveX, moveZ, events) {
    if (hasEffect(p, "no_dash"))
        return false;
    if (hasEffect(p, "dash"))
        return false;
    // Not out of a roll, a dash, a ride or a leap: the travel owns the body
    // until it lands. `stepActions` holds the press, like it does through a
    // wind-up, so the dash fires on arrival.
    if (isTravelling(p))
        return false;
    if (world.tick < (p.cooldowns[DASH_ABILITY_ID] ?? 0))
        return false;
    const fwd = forwardFromYaw(p.yaw);
    let dx = fwd.x;
    let dz = fwd.z;
    const mag = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (mag > 0.01) {
        // Dash where the stick points if the stick is pointing anywhere; otherwise
        // dash where you are looking. The right vector MUST be `rightFromYaw` — a
        // hand-rolled `(fwd.z, -fwd.x)` here is its exact negation, which mirrored
        // every strafing dash across the forward axis: hold D, dash left.
        const right = rightFromYaw(p.yaw);
        const rx = fwd.x * moveZ + right.x * moveX;
        const rz = fwd.z * moveZ + right.z * moveX;
        const rlen = Math.sqrt(rx * rx + rz * rz);
        if (rlen > 1e-6) {
            dx = rx / rlen;
            dz = rz / rlen;
        }
    }
    p.cooldowns[DASH_ABILITY_ID] = world.tick + msToTicks(DASH_COOLDOWN_MS);
    applyEffect(p, "dash", DASH_DURATION_MS, world.tick, p.id, { ax: dx, az: dz });
    applyEffect(p, "iframe", DASH_DURATION_MS, world.tick, p.id);
    // A dash reads as a horizontal move. Reduced in-dash gravity already sells
    // that from a standstill, but it cannot un-earn velocity: dashing at terminal
    // velocity stayed a full-speed dive. Shed the downward component instead.
    if (p.vy < 0)
        p.vy = 0;
    events.push({
        t: "ability_cast",
        tick: world.tick,
        playerId: p.id,
        abilityId: DASH_ABILITY_ID,
        x: p.x,
        z: p.z,
        yaw: p.yaw,
    });
    return true;
}
// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------
/**
 * Resolves every cast whose wind-up has elapsed.
 *
 * Casts are appended in sorted-player order every tick and drained in array
 * order, so resolution order is stable across runtimes.
 */
export function resolvePendingCasts(world, events) {
    if (world.casts.length === 0)
        return;
    const pending = world.casts;
    // A strike that starts a travel queues its own arrival cast; it lands in
    // this fresh array rather than in the one being drained, and is appended
    // after everything kept — a fixed order, the same on every runtime.
    world.casts = [];
    const keep = [];
    for (const cast of pending) {
        if (world.tick < cast.resolveTick) {
            keep.push(cast);
            continue;
        }
        const caster = findPlayer(world, cast.playerId);
        // Dying during your own wind-up cancels the cast. Fault Line landing from
        // beyond the grave would make trading with Monolith strictly bad.
        if (!caster || caster.phase !== "alive")
            continue;
        const ability = findAbilityById(caster.characterId, cast.abilityId);
        if (!ability)
            continue;
        resolveAbility(world, caster, ability, cast, events);
    }
    world.casts = keep.concat(world.casts);
}
/**
 * Queues the second half of a cast that travels: the same cast, resolving
 * when the travel ends, so the cone is cut from where the roll stopped and
 * the hover begins at the leap's apex.
 */
function queueArrival(world, cast, ticks) {
    world.casts.push({ ...cast, resolveTick: world.tick + ticks, stage: "arrive" });
}
/**
 * Extends the caster's commitment over a travel. `busy` is authored as
 * wind-up plus recovery; a travel that outlasts the recovery would otherwise
 * let a second cast start mid-roll, with the arrival still pending. One tick
 * past the last step, so the arrival resolves before anything new can start.
 */
function holdBusy(world, p, ticks) {
    applyEffect(p, "busy", ticksToMs(ticks + 1), world.tick, p.id);
}
/**
 * Ends a travel now and brings its arrival forward to this tick. The roll
 * that met a body two ticks early cuts two ticks early — from where it
 * stopped, which is the whole point of stopping there.
 */
export function arriveNow(world, p) {
    endTravel(p);
    for (const c of world.casts) {
        if (c.playerId === p.id && c.stage === "arrive")
            c.resolveTick = world.tick;
    }
}
/**
 * Parallel Path's cut, one tick of it: every enemy body the dash's rectangle
 * covered between where the caster was and where they are now, once each.
 * Called from the step after the body has moved and met its walls, so the
 * segment is the ground actually crossed. Same rounded rectangle and body pad
 * as the instant line it replaces; the ticks' segments tile the same 9 m.
 *
 * A body far above or below the dash is not cut: the dash is the caster's own
 * body travelling, and it has the reach of one.
 */
export function stepTravelHits(world, p, prevX, prevZ, events) {
    const aux = getAux(world, p.id);
    const ability = findAbilityById(p.characterId, aux.travelAbilityId);
    if (!ability || ability.hit.kind !== "dash_line")
        return;
    const damage = ability.damage * getCharacter(p.characterId).damageMult;
    const effects = ability.effects ?? [];
    const halfWidth = ability.hit.width * 0.5;
    const bodyR = bodyRadius(world);
    const pad2 = bodyR * bodyR;
    for (const target of world.players) {
        if (!isEnemy(p, target) || aux.struck.includes(target.id))
            continue;
        if (Math.abs(target.y - p.y) > MELEE_VERTICAL_REACH)
            continue;
        if (pointRectDist2XZ(target.x, target.z, prevX, prevZ, p.x, p.z, halfWidth) > pad2)
            continue;
        hitTarget(world, p, target, ability, damage, effects, events, prevX, prevZ);
        aux.struck.push(target.id);
    }
    destroyDecoysOnSegment(world, prevX, prevZ, p.x, p.z, halfWidth, events);
    // Crates the dash passes through, once each: the same `struck` list keeps a
    // crate the dash spans for two ticks from being cut twice.
    damageCratesWhere(world, (c) => {
        if (aux.struck.includes(c.id))
            return false;
        if (Math.abs(c.y - p.y) > MELEE_VERTICAL_REACH)
            return false;
        if (pointRectDist2XZ(c.x, c.z, prevX, prevZ, p.x, p.z, halfWidth) > CRATE_RADIUS * CRATE_RADIUS)
            return false;
        aux.struck.push(c.id);
        return true;
    }, damage, p.id, ability.id, events);
}
function findAbilityById(characterId, abilityId) {
    const c = getCharacter(characterId);
    for (const slot of SLOTS) {
        const a = c.abilities[slot];
        if (a.id === abilityId)
            return a;
    }
    return null;
}
function resolveAbility(world, caster, ability, cast, events) {
    const effects = ability.effects ?? [];
    const damage = ability.damage * getCharacter(caster.characterId).damageMult;
    const yaw = cast.yaw;
    // WORLD-anchored shapes resolve from the FROZEN cast origin: their telegraph
    // is a place drawn on the floor at commit, so reading caster.x/z would let
    // anyone who kept moving through their own wind-up drag the real shape off
    // the drawn one — stepping off the decal is dodging, and it has to work.
    // BODY-anchored shapes resolve from the LIVE body: freezing them (the first,
    // too-broad form of that fix) left a 90ms melee cone hanging in the air
    // behind a walking caster while the client drew it on the moving character.
    // The split is @chaos/protocol's abilityAnchor, shared with the client's
    // telegraphs and VFX, so hit, telegraph and effect stay the same place.
    const frozen = abilityAnchor(ability) === "world";
    const ox = frozen ? cast.x : caster.x;
    const oy = frozen ? cast.y : caster.y;
    const oz = frozen ? cast.z : caster.z;
    const fwd = forwardFromYaw(yaw);
    switch (ability.hit.kind) {
        case "self": {
            // The one shape with no origin: it targets the caster's live body, so
            // there is nothing to freeze — Overload buffs Volt wherever he is, and
            // Perch lifts Talon from where he actually stands, not from where he
            // pressed the button.
            const lift = ability.selfMove ?? 0;
            if (cast.stage === "strike" && lift > 0 && (ability.selfMoveMs ?? 0) > 0) {
                // Hover Slam's leap: straight up on the ease-out, and the hover — the
                // ability's effects — begins at the apex, from the arrival cast. An
                // 8 m pop in one tick was the tallest teleport in the game.
                const ticks = travelTicks(lift, msToTicks(ability.selfMoveMs ?? 0));
                if (!startTravel(world, caster, "leap", 0, 1, 0, lift, ticks))
                    break;
                queueArrival(world, cast, ticks);
                holdBusy(world, caster, ticks);
                reportEffect(world, events, caster.id, "leap", ticksToMs(ticks));
                break;
            }
            applyAbilityEffects(world, caster.id, caster, effects, events);
            if (lift > 0) {
                // Arrived at the apex (or an instant lift, which nothing authored is).
                if (cast.stage === "strike")
                    caster.y += lift;
                caster.vy = 0;
            }
            break;
        }
        case "melee_cone": {
            // The lunge: NVDA's basic carries the body forward before it cuts, as a
            // ROLL over `selfMoveMs`, stopping at walls and at the first enemy body
            // in its way, and the cone is cut when the roll arrives — from the
            // arrival cast, at the live body's NEW place.
            const lunge = ability.selfMove ?? 0;
            if (cast.stage === "strike" && lunge > 0 && (ability.selfMoveMs ?? 0) > 0) {
                const ticks = travelTicks(lunge, msToTicks(ability.selfMoveMs ?? 0));
                if (!startTravel(world, caster, "lunge", fwd.x, 0, fwd.z, lunge, ticks))
                    break;
                queueArrival(world, cast, ticks);
                holdBusy(world, caster, ticks);
                reportEffect(world, events, caster.id, "lunge", ticksToMs(ticks));
                break;
            }
            let coneX = ox;
            let coneZ = oz;
            if (cast.stage === "strike" && lunge > 0) {
                // An instant lunge — no `selfMoveMs` authored. The same wall-stopped
                // sweep the roll meets tick by tick, taken whole.
                const swept = sweepPoint(caster.x, caster.y, caster.z, fwd.x, fwd.z, lungeClearance(world, caster, fwd.x, fwd.z, lunge), world.platforms, undefined, chaosPlayerScale(world));
                caster.x = swept.x;
                caster.z = swept.z;
                coneX = swept.x;
                coneZ = swept.z;
            }
            const bodyR = bodyRadius(world);
            const range = ability.hit.range + bodyR;
            const cosHalf = cosDeg(ability.hit.halfAngleDeg);
            const sinHalf = sinDeg(ability.hit.halfAngleDeg);
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                // A swing has a height. Without this a grounded basic reached a Perched
                // Talon 8m straight up, at full damage.
                if (Math.abs(target.y - oy) > MELEE_VERTICAL_REACH)
                    continue;
                if (!inWedge(target.x - coneX, target.z - coneZ, fwd.x, fwd.z, range, cosHalf, sinHalf, bodyR))
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, coneX, coneZ);
            }
            destroyDecoysInSphere(world, coneX, oy, coneZ, range, events);
            // The same wedge, padded by the crate's footprint instead of a body's.
            const crateRange = ability.hit.range + CRATE_RADIUS;
            damageCratesWhere(world, (v) => Math.abs(v.y - oy) <= MELEE_VERTICAL_REACH &&
                inWedge(v.x - coneX, v.z - coneZ, fwd.x, fwd.z, crateRange, cosHalf, sinHalf, CRATE_RADIUS), damage, caster.id, ability.id, events);
            break;
        }
        case "shot": {
            // Bullets leave the LIVE body (body-anchored) along the FROZEN yaw. A
            // burst cast carries the index of the one pellet it fires; a spread or a
            // single fires its whole volley here.
            const shape = ability.hit;
            if (cast.pellet >= 0) {
                spawnBullet(world, caster, ability, damage, yaw, cast.pellet);
            }
            else {
                for (let i = 0; i < shape.pellets; i++)
                    spawnBullet(world, caster, ability, damage, yaw, i);
            }
            break;
        }
        case "sphere": {
            // One hit grammar everywhere: cones and lines already pad by body radius,
            // so "my body touches the drawn circle" must be a hit here too. Without
            // the pad, circles under-delivered by 0.5m while cones over-delivered —
            // two rules the player was never told apart.
            const radius = ability.hit.radius + bodyRadius(world);
            const chain = findEffectDef(effects, "chain");
            const swap = findEffectDef(effects, "swap_position");
            const pull = findEffectDef(effects, "pull");
            if (swap) {
                resolveSwap(world, caster, ability, ox, oy, oz, radius, events);
                break;
            }
            if (chain) {
                resolveChain(world, caster, ability, damage, ox, oy, oz, radius, chain, effects, events);
                break;
            }
            if (pull) {
                // Gravity Well is body-anchored: it drags everyone toward where Hex
                // actually stands at resolve, the point the tracking decal has
                // followed her to.
                for (const target of world.players) {
                    if (!isEnemy(caster, target))
                        continue;
                    if (casualProtected(world, target))
                        continue;
                    if (dist2XZ(target.x, target.z, ox, oz) > radius * radius)
                        continue;
                    applyEffect(target, "pull", PULL_DURATION_MS, world.tick, caster.id, {
                        magnitude: pull.kind === "pull" ? pull.strength : 0,
                        ax: ox,
                        az: oz,
                    });
                    events.push({
                        t: "effect_applied",
                        tick: world.tick,
                        playerId: target.id,
                        effect: "pull",
                        durationMs: PULL_DURATION_MS,
                    });
                }
                break;
            }
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                if (dist2(target.x, target.y, target.z, ox, oy, oz) > radius * radius)
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, ox, oz);
            }
            destroyDecoysInSphere(world, ox, oy, oz, radius, events);
            // The same SPHERE bodies are tested against, measured to the nearest
            // point of the crate's vertical axis and padded by its footprint.
            const crateReach = ability.hit.radius + CRATE_RADIUS;
            damageCratesWhere(world, (v) => {
                const vy = Math.max(0, Math.abs(v.y - oy) - CRATE_HALF_HEIGHT);
                return dist2XZ(v.x, v.z, ox, oz) + vy * vy <= crateReach * crateReach;
            }, damage, caster.id, ability.id, events);
            break;
        }
        case "dash_line": {
            const length = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : ability.hit.length;
            const startX = ox;
            const startZ = oz;
            let endX = ox + fwd.x * length;
            let endZ = oz + fwd.z * length;
            // Blink Strike moves the caster; Fault Line only cracks the ground. The
            // anchor split above already picked the right start: Fault Line's line
            // is world-anchored (ox/oz frozen, the crack stays on the drawn floor),
            // while Blink Strike is your own body dashing, so its line runs from
            // wherever you actually are at resolve — sweeping a displaced caster
            // from the stale cast position would teleport them back to it first.
            if ((ability.selfMove ?? 0) > 0) {
                if ((ability.selfMoveMs ?? 0) > 0) {
                    // Parallel Path: a DASH the body travels over `selfMoveMs`, cutting
                    // every body it passes through on the way (`stepTravelHits`, per
                    // tick). The line is the same 9 m; it is simply no longer crossed
                    // inside one tick.
                    const ticks = travelTicks(length, msToTicks(ability.selfMoveMs ?? 0));
                    if (!startTravel(world, caster, "blink", fwd.x, 0, fwd.z, length, ticks))
                        break;
                    const aux = getAux(world, caster.id);
                    aux.struck.length = 0;
                    aux.travelAbilityId = ability.id;
                    holdBusy(world, caster, ticks);
                    reportEffect(world, events, caster.id, "blink", ticksToMs(ticks));
                    break;
                }
                // The sweep is a PLAYER moving through the world, so it uses the same
                // body as everything else — a giant blinks a shorter distance because
                // it is blocked sooner, which is the trade the mutator is making.
                const swept = sweepPoint(ox, oy, oz, fwd.x, fwd.z, length, world.platforms, undefined, chaosPlayerScale(world));
                caster.x = swept.x;
                caster.z = swept.z;
                endX = swept.x;
                endZ = swept.z;
            }
            // Rectangle plus body pad, NOT a capsule around the spine: the capsule
            // form put a radial cap of the full half-width on the segment end, so
            // the drawn tip under-promised by width/2 — Fault Line hit 1.5m past
            // the end of its own decal. Now every edge over-delivers by exactly the
            // body radius, the one grammar all the other shapes speak.
            const halfWidth = ability.hit.width * 0.5;
            const bodyR = bodyRadius(world);
            const pad2 = bodyR * bodyR;
            for (const target of world.players) {
                if (!isEnemy(caster, target))
                    continue;
                if (pointRectDist2XZ(target.x, target.z, startX, startZ, endX, endZ, halfWidth) > pad2)
                    continue;
                hitTarget(world, caster, target, ability, damage, effects, events, startX, startZ);
            }
            destroyDecoysOnSegment(world, startX, startZ, endX, endZ, halfWidth, events);
            damageCratesWhere(world, (v) => pointRectDist2XZ(v.x, v.z, startX, startZ, endX, endZ, halfWidth) <= CRATE_RADIUS * CRATE_RADIUS, damage, caster.id, ability.id, events);
            break;
        }
        case "projectile": {
            spawnProjectile(world, caster, ability, damage, yaw, ox, oy, oz);
            break;
        }
        case "ground_zone": {
            spawnGroundZone(world, caster, ability, damage, effects, yaw, ox, oy, oz);
            break;
        }
    }
    // Effects that live on the caster regardless of what the shape hit.
    const decoy = findEffectDef(effects, "decoy");
    if (decoy && decoy.kind === "decoy")
        spawnDecoy(world, caster, decoy.durationMs);
}
function isEnemy(caster, target) {
    return target.id !== caster.id && target.phase === "alive";
}
function hitTarget(world, caster, target, ability, damage, effects, events, 
// Where the hit came from: the shape's own resolve origin (frozen for
// world-anchored abilities, the live body for body-anchored ones, the
// previous link for a chain), so knockback radiates out of the shape that
// actually hit rather than away from somewhere else entirely.
fromX, fromZ) {
    // Zero-damage kit (pulls, displaces) would otherwise still move a shielded
    // body, and a body still loading the arena can be dragged off a ledge.
    if (casualProtected(world, target))
        return 0;
    const applied = applyDamage(world, target, damage, caster.id, ability.id, events);
    // Immunity blocks the rider effects too — i-frames through a Fault Line means
    // through the stun as well, or the dash never actually saved you.
    if (damage > 0 && applied <= 0)
        return 0;
    applyKnockback(target, fromX, fromZ, ability.knockback ?? 0);
    applyAbilityEffects(world, caster.id, target, effects, events);
    return applied;
}
function applyKnockback(target, fromX, fromZ, metres) {
    if (metres <= 0)
        return;
    const dx = target.x - fromX;
    const dz = target.z - fromZ;
    const d = Math.sqrt(dx * dx + dz * dz);
    const speed = metres * KNOCKBACK_SPEED_PER_UNIT;
    if (d < 1e-6) {
        target.vy += speed * KNOCKBACK_LIFT_FRACTION;
        return;
    }
    target.vx += (dx / d) * speed;
    target.vz += (dz / d) * speed;
    target.vy += speed * KNOCKBACK_LIFT_FRACTION;
}
function applyAbilityEffects(world, casterId, target, effects, events) {
    // Every hostile rider (stun, slow, pull, zero-damage traps) comes through
    // here; the shield blocks them as it blocks damage.
    if (target.id !== casterId && casualProtected(world, target))
        return;
    for (const e of effects) {
        switch (e.kind) {
            case "slow":
            case "speed":
            case "damage_reduction":
                applyEffect(target, e.kind, e.durationMs, world.tick, casterId, { magnitude: e.pct });
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                // Volt's Overload states slow immunity in prose; this is that, as state.
                if (e.kind === "speed" && target.id === casterId) {
                    applyEffect(target, "slow_immune", e.durationMs, world.tick, casterId);
                }
                break;
            case "stun":
            case "invisible":
            case "no_dash":
            case "hover":
            case "suppress_abilities":
                applyEffect(target, e.kind, e.durationMs, world.tick, casterId);
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                break;
            case "damage_over_time":
                applyEffect(target, "damage_over_time", e.durationMs, world.tick, casterId, { magnitude: e.dps });
                reportEffect(world, events, target.id, e.kind, e.durationMs);
                break;
            // Handled by the shape resolver, not here.
            case "pull":
            case "swap_position":
            case "chain":
            case "repeat":
            case "decoy":
            case "refund_cooldown_on_kill":
                break;
        }
    }
}
function reportEffect(world, events, playerId, effect, durationMs) {
    events.push({ t: "effect_applied", tick: world.tick, playerId, effect, durationMs });
}
function findEffectDef(effects, kind) {
    for (const e of effects)
        if (e.kind === kind)
            return e;
    return null;
}
/**
 * Mirage's Robotaxi: the RIDE. The nearest valid target inside the radius and
 * the caster travel to each other's spot, on the same ease-out over the same
 * ticks, passing each other in the middle. It trades the two live bodies
 * exactly as the swap did — through walls, onto the ground the other stood on
 * — only over a quarter of a second instead of inside one tick, which is the
 * difference between a ride and a teleport on every screen watching.
 *
 * Body-anchored, so acquisition measures from Mirage's live position — the
 * circle travels with her. A long ride stretches: see `travelTicks`.
 */
function resolveSwap(world, caster, ability, ox, oy, oz, radius, events) {
    const target = nearestTarget(world, caster, ox, oy, oz, radius);
    if (!target)
        return;
    const minTicks = msToTicks(ability.selfMoveMs ?? 0);
    const cx = caster.x;
    const cy = caster.y;
    const cz = caster.z;
    if (target.kind === "decoy") {
        // Riding to a decoy is a legal, funny outcome. It still moves you — and
        // the decoy takes the taxi the other way, linearly, on its own ticks.
        const d = target.decoy;
        const ticks = travelTicks(dist3(cx, cy, cz, d.x, d.y, d.z), minTicks);
        rideTo(world, caster, d.x, d.y, d.z, ticks);
        d.rideX = cx;
        d.rideZ = cz;
        d.rideLeft = ticks;
        d.vx = 0;
        d.vz = 0;
        holdBusy(world, caster, ticks);
        reportEffect(world, events, caster.id, "ride", ticksToMs(ticks));
        return;
    }
    const other = target.player;
    const ticks = travelTicks(dist3(cx, cy, cz, other.x, other.y, other.z), minTicks);
    rideTo(world, caster, other.x, other.y, other.z, ticks);
    rideTo(world, other, cx, cy, cz, ticks);
    holdBusy(world, caster, ticks);
    reportEffect(world, events, caster.id, "ride", ticksToMs(ticks));
    reportEffect(world, events, other.id, "ride", ticksToMs(ticks));
    events.push({
        t: "effect_applied",
        tick: world.tick,
        playerId: other.id,
        effect: "swap_position",
        durationMs: ticksToMs(ticks),
    });
}
/** Starts a ride from where `p` stands to (tx, ty, tz), in 3D, over `ticks`. */
function rideTo(world, p, tx, ty, tz, ticks) {
    const dx = tx - p.x;
    const dy = ty - p.y;
    const dz = tz - p.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // Already there: nothing to ride, and a zero direction would be a NaN.
    if (d < 1e-6)
        return;
    startTravel(world, p, "ride", dx / d, dy / d, dz / d, d, ticks);
}
/** Volt's Chain Lightning: nearest target, then two arcs at 60% each. */
function resolveChain(world, caster, ability, damage, ox, oy, oz, radius, chain, effects, events) {
    if (chain.kind !== "chain")
        return;
    // First strike acquires from Volt's live body — the lightning arcs out of
    // him, not out of a spot he left — and hops then arc from wherever each
    // struck body actually is: the chain is drawn link to link.
    const first = nearestTarget(world, caster, ox, oy, oz, radius);
    if (!first)
        return;
    if (first.kind === "decoy") {
        // The decoy eats the ultimate. That is the entire point of the decoy.
        destroyDecoy(world, first.decoy.id, caster.id, ability.id, damage, events);
        return;
    }
    const struck = new Set();
    let current = first.player;
    let dmg = damage;
    hitTarget(world, caster, current, ability, dmg, effects, events, ox, oz);
    struck.add(current.id);
    for (let hop = 0; hop < chain.maxTargets - 1; hop++) {
        let best = null;
        // Padded by body radius like acquisition above and every other distance in
        // the game — this was the one unpadded check, so a body grazing the hop
        // range escaped an arc that the same body would have caught anywhere else.
        const hopRange = chain.radius + bodyRadius(world);
        let bestD2 = hopRange * hopRange;
        for (const candidate of world.players) {
            if (!isEnemy(caster, candidate) || struck.has(candidate.id))
                continue;
            const d2 = dist2(candidate.x, candidate.y, candidate.z, current.x, current.y, current.z);
            if (d2 <= bestD2) {
                bestD2 = d2;
                best = candidate;
            }
        }
        if (!best)
            break;
        dmg = (dmg * chain.falloffPct) / 100;
        hitTarget(world, caster, best, ability, dmg, effects, events, current.x, current.z);
        struck.add(best.id);
        current = best;
    }
}
/**
 * Nearest enemy or decoy, ties broken by id so both runtimes agree.
 *
 * Decoys are candidates here on purpose: a decoy that could not be picked as
 * "nearest target" would be identified instantly by every single-target
 * ability in the game.
 */
function nearestTarget(world, caster, ox, oy, oz, radius) {
    let best = null;
    let bestD2 = radius * radius;
    let bestId = "";
    for (const p of world.players) {
        if (!isEnemy(caster, p))
            continue;
        // Shielded practice bodies are not targets: a swap would move one and a
        // chain would spend its first link on it.
        if (casualProtected(world, p))
            continue;
        const d2 = dist2(p.x, p.y, p.z, ox, oy, oz);
        if (d2 < bestD2 || (d2 === bestD2 && best !== null && p.id < bestId)) {
            bestD2 = d2;
            best = { kind: "player", player: p };
            bestId = p.id;
        }
    }
    for (const d of world.decoys) {
        if (d.ownerId === caster.id)
            continue;
        const d2 = dist2(d.x, d.y, d.z, ox, oy, oz);
        if (d2 < bestD2 || (d2 === bestD2 && best !== null && d.id < bestId)) {
            bestD2 = d2;
            best = { kind: "decoy", decoy: d };
            bestId = d.id;
        }
    }
    return best;
}
// ---------------------------------------------------------------------------
// Projectiles — Falcon's lobbed shell, and every basic-attack bullet
// ---------------------------------------------------------------------------
function spawnProjectile(world, caster, ability, damage, yaw, ox, oy, oz) {
    if (ability.hit.kind !== "projectile")
        return;
    const fwd = forwardFromYaw(yaw);
    const speed = ability.hit.speed;
    world.nextEntityId += 1;
    world.projectiles.push({
        id: `proj#${world.nextEntityId}`,
        ownerId: caster.id,
        abilityId: ability.id,
        // Launched from the frozen cast origin: the wind-up flash is drawn there,
        // and a barrel that follows the caster re-aims the shot the yaw freeze
        // exists to pin down.
        x: ox + fwd.x * PROJECTILE_SPAWN_OFFSET,
        y: oy + PROJECTILE_MUZZLE_HEIGHT,
        z: oz + fwd.z * PROJECTILE_SPAWN_OFFSET,
        vx: fwd.x * speed * PROJECTILE_LAUNCH_COS,
        vy: speed * PROJECTILE_LAUNCH_SIN,
        vz: fwd.z * speed * PROJECTILE_LAUNCH_COS,
        gravity: -ability.hit.gravity,
        radius: ability.hit.radius,
        contactRadius: PROJECTILE_CONTACT_RADIUS,
        damage,
        remainingRange: ability.hit.maxRange,
        knockback: ability.knockback ?? 0,
        pierce: false,
        struck: [],
    });
}
/**
 * One bullet of a basic attack. Flat flight: no gravity, no arc. Leaves the
 * caster's LIVE body at chest height, down the frozen yaw plus this pellet's
 * deterministic fan offset. `damage` is per bullet, already multiplied.
 */
function spawnBullet(world, caster, ability, damage, yaw, pellet) {
    if (ability.hit.kind !== "shot")
        return;
    const shape = ability.hit;
    const dir = forwardFromYaw(yaw + pelletOffsetRad(shape, pellet));
    // A round pressed against a wall dies in the barrel. The spawn point sits
    // PROJECTILE_SPAWN_OFFSET ahead of the body and the flight test never looks
    // at the spawn point itself, so a shooter hugging a one-unit wall started
    // the round inside it and the next wall sample landed past the far face —
    // a shot through cover at whoever stood behind it.
    if (!muzzleClear(world, caster.x, caster.y + PROJECTILE_MUZZLE_HEIGHT, caster.z, dir.x, dir.z))
        return;
    world.nextEntityId += 1;
    world.projectiles.push({
        id: `proj#${world.nextEntityId}`,
        ownerId: caster.id,
        abilityId: ability.id,
        x: caster.x + dir.x * PROJECTILE_SPAWN_OFFSET,
        y: caster.y + PROJECTILE_MUZZLE_HEIGHT,
        z: caster.z + dir.z * PROJECTILE_SPAWN_OFFSET,
        vx: dir.x * shape.speed,
        vy: 0,
        vz: dir.z * shape.speed,
        gravity: 0,
        radius: 0,
        contactRadius: shape.radius,
        damage,
        remainingRange: shape.maxRange,
        knockback: ability.knockback ?? 0,
        pierce: shape.pierce,
        struck: [],
    });
}
/**
 * Advances every projectile one tick.
 *
 * SWEPT, not sampled. A bullet at 46 u/s moves 1.5 units a tick and a body is
 * 1 unit wide, so testing only where the bullet ENDS the tick let it pass
 * clean through a player standing in the middle of the step. Each projectile
 * is now a segment from where it was to where it is, tested against every
 * enemy capsule and against the walls in sub-steps of `PROJECTILE_WALL_STEP`.
 * The first thing along the segment wins — wall before body if the wall is
 * nearer — so a shot cannot hit someone standing behind the cover it stopped
 * on. Decoys are along-the-segment candidates too: a decoy a bullet would
 * fly through gives the disguise away.
 *
 * Order is deterministic: projectiles in array order (spawn order, which is
 * sorted-player order), bodies by distance along the segment then by id.
 */
export function stepProjectiles(world, events) {
    if (world.projectiles.length === 0)
        return;
    // Body size under the live chaos scale. Nothing in this step changes the
    // mutations it depends on, so it is read once rather than per projectile.
    const bodyR = bodyRadius(world);
    const halfHeight = bodyHalfHeight(world);
    const keep = [];
    for (const proj of world.projectiles) {
        proj.vy += proj.gravity * DT;
        const sx = proj.x;
        const sy = proj.y;
        const sz = proj.z;
        const dx = proj.vx * DT;
        const dy = proj.vy * DT;
        const dz = proj.vz * DT;
        const stepLen = Math.sqrt(dx * dx + dz * dz);
        // Where along [0, 1] the segment ends: the first wall, the end of the
        // range, or the floor — whichever comes first.
        let endT = 1;
        let ended = null;
        if (proj.remainingRange <= stepLen) {
            endT = stepLen > 0 ? proj.remainingRange / stepLen : 0;
            ended = "range";
        }
        const wallT = firstWallAlong(world, sx, sy, sz, dx, dy, dz);
        if (wallT < endT) {
            endT = wallT;
            ended = "wall";
        }
        const floorT = floorAlong(world, sy, dy);
        if (floorT < endT) {
            endT = floorT;
            ended = "floor";
        }
        // Bodies along the segment. A piercing orb strikes every body it crosses
        // and keeps going. Anything else — a direct-hit round or a shell — is
        // resolved against THE NEAREST THING ONLY, body or decoy, and only after
        // the whole segment has been looked at: a step is up to 1.5 u long, so two
        // bodies a shoulder apart can both lie on it, and the one behind must be
        // spared exactly as if they stood four units apart.
        const reach = bodyR + proj.contactRadius;
        let bodyT = Infinity;
        let firstBody = null;
        for (const p of world.players) {
            if (p.id === proj.ownerId || p.phase !== "alive")
                continue;
            if (proj.pierce && proj.struck.includes(p.id))
                continue;
            const t = segmentCapsuleT(sx, sy, sz, dx, dy, dz, endT, p.x, p.y, p.z, reach, halfHeight);
            if (t < 0)
                continue;
            if (proj.pierce) {
                const applied = applyDamage(world, p, proj.damage, proj.ownerId, proj.abilityId, events);
                if (applied > 0) {
                    applyKnockback(p, sx + dx * t, sz + dz * t, proj.knockback);
                }
                proj.struck.push(p.id);
                continue;
            }
            if (t < bodyT) {
                bodyT = t;
                firstBody = p;
            }
        }
        if (proj.pierce) {
            // A piercing orb pops every decoy on its way as it does every body, and
            // cracks every crate — once each, like a body.
            popDecoysAlong(world, proj, sx, sy, sz, dx, dy, dz, endT, reach, halfHeight, events);
            for (const hit of cratesAlong(world, sx, sy, sz, dx, dy, dz, endT, proj.contactRadius, proj.ownerId)) {
                if (proj.struck.includes(hit.crate.id))
                    continue;
                proj.struck.push(hit.crate.id);
                damageCrate(world, hit.crate, proj.damage, proj.ownerId, proj.abilityId, events);
            }
        }
        else {
            // The nearest decoy competes with the nearest body; whichever is
            // nearer is where the bullet stops, and only that one is touched. A
            // crate competes the same way: a lockbox between you and your target
            // is cover.
            const decoy = nearestDecoyAlong(world, proj, sx, sy, sz, dx, dy, dz, Math.min(endT, bodyT), reach, halfHeight);
            // A crate takes the round only when it is STRICTLY nearer than the body,
            // and an OCCUPIED crate is not cover at all. Crates do not block
            // movement, so a player can stand in one — and because a crate's reach is
            // wider than a body's, the round met the lockbox a step before the body
            // inside it, and that player was bullet-proof. Whoever stands in a crate
            // is standing in the open.
            const crate = nearestCrateAlong(world, proj, sx, sy, sz, dx, dy, dz, Math.min(endT, bodyT, decoy?.t ?? Infinity), bodyT, bodyR);
            if (crate !== null) {
                bodyT = crateT;
                // A shell bursts on it below and splashes it there instead.
                if (proj.radius === 0)
                    damageCrate(world, crate, proj.damage, proj.ownerId, proj.abilityId, events);
            }
            else if (decoy !== null && decoy.t < bodyT) {
                bodyT = decoy.t;
                destroyDecoy(world, decoy.id, proj.ownerId, proj.abilityId, proj.damage, events);
            }
            else if (firstBody !== null && proj.radius === 0) {
                // A direct hit. A shell (radius > 0) detonates on it below instead.
                const applied = applyDamage(world, firstBody, proj.damage, proj.ownerId, proj.abilityId, events);
                if (applied > 0) {
                    applyKnockback(firstBody, sx + dx * bodyT, sz + dz * bodyT, proj.knockback);
                }
            }
        }
        if (bodyT <= endT) {
            endT = bodyT;
            ended = "body";
        }
        proj.x = sx + dx * endT;
        proj.y = sy + dy * endT;
        proj.z = sz + dz * endT;
        proj.remainingRange -= stepLen * endT;
        if (ended !== null || proj.y < world.map.killY) {
            if (proj.radius > 0)
                detonate(world, proj, events);
            continue;
        }
        keep.push(proj);
    }
    world.projectiles = keep;
}
/**
 * How far a flat shot from (x, y, z) along (dirX, dirZ) travels before a solid
 * wall, capped at `maxRange`. Pure geometry against the world's platforms —
 * the client calls this from its predicted world to end a drawn bullet on the
 * wall the real one will stop at, without a message.
 */
export function traceShot(world, x, y, z, dirX, dirZ, maxRange) {
    const t = firstWallAlong(world, x, y, z, dirX * maxRange, 0, dirZ * maxRange);
    return t >= 1 ? maxRange : t * maxRange;
}
/**
 * Whether the barrel is open: no wall between the body centre at muzzle height
 * (x, y, z) and the spawn point PROJECTILE_SPAWN_OFFSET ahead along (dirX, dirZ).
 * The server drops a round whose barrel is blocked; the client asks the same
 * question so it never draws one.
 */
export function muzzleClear(world, x, y, z, dirX, dirZ) {
    return firstWallAlong(world, x, y, z, dirX * PROJECTILE_SPAWN_OFFSET, 0, dirZ * PROJECTILE_SPAWN_OFFSET) > 1;
}
const projectileCandidates = [];
/** Parameter in [0, 1] of the first wall sample along the segment, or 2 if none. */
function firstWallAlong(world, sx, sy, sz, dx, dy, dz) {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const samples = Math.max(1, Math.ceil(len / PROJECTILE_WALL_STEP));
    for (let i = 1; i <= samples; i++) {
        const t = i / samples;
        if (insideSolid(world, sx + dx * t, sy + dy * t, sz + dz * t))
            return t;
    }
    return 2;
}
function insideSolid(world, x, y, z) {
    const candidates = world.hash.queryInto(x, z, projectileCandidates);
    for (const index of candidates) {
        const plat = world.platforms[index];
        if (!plat || !plat.solid)
            continue;
        if (Math.abs(x - plat.x) <= plat.hx && Math.abs(y - plat.y) <= plat.hy && Math.abs(z - plat.z) <= plat.hz) {
            return true;
        }
    }
    return false;
}
/** Parameter at which a falling projectile meets the map's floor plane, or 2. */
function floorAlong(world, sy, dy) {
    if (world.map.floor.kind === "none")
        return 2;
    const floorY = world.map.floor.y;
    if (sy + dy > floorY)
        return 2;
    if (dy >= 0)
        return sy <= floorY ? 0 : 2;
    return clamp((floorY - sy) / dy, 0, 1);
}
/**
 * Where along the segment [0, endT] a projectile first touches a capsule at
 * (px, py, pz), or -1. Closest approach in XZ, then the real rounded cap on
 * the height: the same point-vs-capsule test the old end-of-tick check did,
 * applied at the nearest point of the sweep instead of only at its end.
 */
function segmentCapsuleT(sx, sy, sz, dx, dy, dz, endT, px, py, pz, reach, halfHeight) {
    const ex = sx + dx * endT;
    const ez = sz + dz * endT;
    if (pointSegmentDist2XZ(px, pz, sx, sz, ex, ez) > reach * reach)
        return -1;
    // Parameter of the closest approach, in [0, endT].
    const abx = dx * endT;
    const abz = dz * endT;
    const ab2 = abx * abx + abz * abz;
    let t = 0;
    if (ab2 > 1e-12)
        t = clamp(((px - sx) * abx + (pz - sz) * abz) / ab2, 0, 1) * endT;
    const y = sy + dy * t;
    const cy = clamp(y, py - halfHeight, py + halfHeight);
    if (dist2(sx + dx * t, y, sz + dz * t, px, cy, pz) > reach * reach)
        return -1;
    // The bullet touches the body a little before its closest approach; report
    // the approach itself — the difference is under a body radius and it keeps
    // "which came first" comparisons simple and exact.
    return t;
}
/**
 * The nearest decoy (not the shooter's own) within reach of the segment up to
 * `limitT`, or null. Found by distance along the segment, never by array
 * order: two decoys a shoulder apart can both lie on one step.
 */
function nearestDecoyAlong(world, proj, sx, sy, sz, dx, dy, dz, limitT, reach, halfHeight) {
    let best = null;
    for (const d of world.decoys) {
        if (d.ownerId === proj.ownerId)
            continue;
        const t = segmentCapsuleT(sx, sy, sz, dx, dy, dz, limitT, d.x, d.y, d.z, reach, halfHeight);
        if (t < 0)
            continue;
        if (best === null || t < best.t)
            best = { id: d.id, t };
    }
    return best;
}
/**
 * Pops every decoy (not the shooter's own) within reach of the segment up to
 * `limitT`. For a piercing projectile only: a direct-hit one resolves the
 * single nearest decoy against the nearest body in `stepProjectiles`.
 */
function popDecoysAlong(world, proj, sx, sy, sz, dx, dy, dz, limitT, reach, halfHeight, events) {
    for (let i = world.decoys.length - 1; i >= 0; i--) {
        const d = world.decoys[i];
        if (!d || d.ownerId === proj.ownerId)
            continue;
        const t = segmentCapsuleT(sx, sy, sz, dx, dy, dz, limitT, d.x, d.y, d.z, reach, halfHeight);
        if (t < 0)
            continue;
        destroyDecoy(world, d.id, proj.ownerId, proj.abilityId, proj.damage, events);
    }
}
/**
 * How far above or below a burst still counts as caught in it.
 *
 * Splash is measured on the GROUND PLANE with a vertical band, not as a 3D
 * sphere. A shell that bursts in the air — because it ran out of range, clipped
 * a wall, or detonated over someone's head — is still an explosion, and a 3D
 * test makes it silently harmless: at radius 2.5 a burst 3m up cannot reach a
 * player standing directly underneath. That is precisely how Arc Shot came to be
 * unable to hit ANY grounded target at ANY distance on flat ground.
 */
const SPLASH_VERTICAL_REACH = 4;
function detonate(world, proj, events) {
    // Padded by body radius: same grammar as every other circle.
    const r = proj.radius + bodyRadius(world);
    const r2 = r * r;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        if (p.id === proj.ownerId)
            continue;
        const dx = p.x - proj.x;
        const dz = p.z - proj.z;
        if (dx * dx + dz * dz > r2)
            continue;
        if (Math.abs(p.y - proj.y) > SPLASH_VERTICAL_REACH)
            continue;
        applyDamage(world, p, proj.damage, proj.ownerId, proj.abilityId, events);
    }
    destroyDecoysInSphere(world, proj.x, proj.y, proj.z, r, events);
    damageCratesWhere(world, (v) => inSplash(v, proj.x, proj.y, proj.z, proj.radius), proj.damage, proj.ownerId, proj.abilityId, events);
}
/**
 * A melee wedge test for something whose centre is (dx, dz) from the cone's
 * origin: within `range`, and inside the wedge or grazing its edge by `pad`.
 * `cross*cos - dot*sin` is the exact distance from the centre to the cone's
 * boundary ray, so a body half-inside the 55° edge counts — the same padding
 * grammar the range already grants. Shared by bodies and crates so the two
 * cone rules can never drift apart.
 */
function inWedge(dx, dz, fwdX, fwdZ, range, cosHalf, sinHalf, pad) {
    const d2 = dx * dx + dz * dz;
    if (d2 > range * range)
        return false;
    const dot = dx * fwdX + dz * fwdZ;
    const cross = Math.abs(dx * fwdZ - dz * fwdX);
    return dot >= Math.sqrt(d2) * cosHalf || cross * cosHalf - dot * sinHalf <= pad;
}
/** A burst of `radius` at (x, y, z) reaches this crate: same vertical band as bodies. */
function inSplash(v, x, y, z, radius) {
    const r = radius + CRATE_RADIUS;
    return dist2XZ(v.x, v.z, x, z) <= r * r && Math.abs(v.y - y) <= SPLASH_VERTICAL_REACH + CRATE_HALF_HEIGHT;
}
/** True when any live body but the shooter's overlaps a crate's footprint. */
function crateOccupied(world, v, shooterId, bodyR) {
    const r = CRATE_RADIUS + bodyR;
    for (const p of world.players) {
        if (p.id === shooterId || p.phase !== "alive")
            continue;
        // An invisible body does not open the crate: a round passing through a
        // box whose HP never moves, on every screen, is a ping on the one player
        // the snapshot hides. They keep the crate as cover instead.
        if (isInvisible(p))
            continue;
        if (Math.abs(p.y - v.y) > CRATE_HALF_HEIGHT + bodyHalfHeight(world))
            continue;
        if (dist2XZ(p.x, p.z, v.x, v.z) <= r * r)
            return true;
    }
    return false;
}
/** Where along the segment `nearestCrateAlong`'s answer lies. Read right after the call. */
let crateT = 0;
/**
 * The crate a direct-hit round stops at this step, or null: the nearest (ties
 * by id) within reach up to `limitT`, strictly nearer than the nearest body
 * (`bodyT`), and not occupied. Runs for every round in flight every tick, so
 * it allocates nothing — the answer's `t` is left in `crateT`.
 */
function nearestCrateAlong(world, proj, sx, sy, sz, dx, dy, dz, limitT, bodyT, bodyR) {
    const loot = world.loot;
    if (!loot || loot.crates.length === 0)
        return null;
    // An invisible shooter's rounds pass through crates: a lockbox losing HP
    // on every screen is a radar ping for the one player the snapshot hides.
    if (isGhost(world, proj.ownerId))
        return null;
    let best = null;
    let bestT = Infinity;
    for (const v of loot.crates) {
        const t = segmentCapsuleT(sx, sy, sz, dx, dy, dz, limitT, v.x, v.y, v.z, CRATE_RADIUS + proj.contactRadius, CRATE_HALF_HEIGHT);
        if (t < 0 || t >= bodyT)
            continue;
        if (t > bestT || (t === bestT && best !== null && v.id > best.id))
            continue;
        if (crateOccupied(world, v, proj.ownerId, bodyR))
            continue;
        best = v;
        bestT = t;
    }
    crateT = bestT;
    return best;
}
/**
 * Crates within reach of a piercing projectile's segment up to `limitT`,
 * nearest first (ties by id), so they crack in the order the orb meets them.
 */
function cratesAlong(world, sx, sy, sz, dx, dy, dz, limitT, contactRadius, ownerId) {
    const loot = world.loot;
    if (!loot || loot.crates.length === 0)
        return [];
    // An invisible shooter's rounds pass through crates: a lockbox losing HP
    // on every screen is a radar ping for the one player the snapshot hides.
    if (isGhost(world, ownerId))
        return [];
    const out = [];
    for (const v of loot.crates) {
        const t = segmentCapsuleT(sx, sy, sz, dx, dy, dz, limitT, v.x, v.y, v.z, CRATE_RADIUS + contactRadius, CRATE_HALF_HEIGHT);
        if (t >= 0)
            out.push({ crate: v, t });
    }
    out.sort((a, b) => a.t - b.t || (a.crate.id < b.crate.id ? -1 : a.crate.id > b.crate.id ? 1 : 0));
    return out;
}
// ---------------------------------------------------------------------------
// Ground zones
// ---------------------------------------------------------------------------
function spawnGroundZone(world, caster, ability, damage, effects, yaw, ox, oy, oz) {
    if (ability.hit.kind !== "ground_zone")
        return;
    const repeat = findEffectDef(effects, "repeat");
    const offset = GROUND_ZONE_OFFSET[ability.id] ?? 0;
    const fwd = forwardFromYaw(yaw);
    const kind = repeat ? "barrage" : ability.damage > 0 ? "trap" : "aura";
    world.nextEntityId += 1;
    const zone = {
        id: `zone#${world.nextEntityId}`,
        ownerId: caster.id,
        abilityId: ability.id,
        kind,
        // Placed off the frozen cast origin, so the circle the shells actually
        // fall in is the circle the decal drew at commit.
        x: ox + fwd.x * offset,
        y: oy,
        z: oz + fwd.z * offset,
        radius: ability.hit.radius,
        damage,
        effects: effects.slice(),
        expiresTick: world.tick + msToTicks(ability.hit.durationMs),
        armedTick: world.tick + (kind === "trap" ? TRAP_ARM_TICKS : 0),
        pulsesLeft: repeat && repeat.kind === "repeat" ? repeat.count : 0,
        pulseIntervalTicks: repeat && repeat.kind === "repeat" ? msToTicks(repeat.intervalMs) : 0,
        nextPulseTick: world.tick,
    };
    world.zones.push(zone);
}
export function stepGroundZones(world, events) {
    if (world.zones.length === 0)
        return;
    const keep = [];
    for (const zone of world.zones) {
        let alive = world.tick < zone.expiresTick;
        if (alive) {
            switch (zone.kind) {
                case "trap":
                    alive = stepTrap(world, zone, events);
                    break;
                case "aura":
                    stepAura(world, zone, events);
                    break;
                case "barrage":
                    alive = stepBarrage(world, zone, events);
                    break;
            }
        }
        if (alive)
            keep.push(zone);
    }
    world.zones = keep;
}
/** Returns false once the trap has been consumed. */
function stepTrap(world, zone, events) {
    if (world.tick < zone.armedTick)
        return true;
    const r = zone.radius + bodyRadius(world);
    for (const p of world.players) {
        if (p.phase !== "alive" || p.id === zone.ownerId)
            continue;
        if (dist2XZ(p.x, p.z, zone.x, zone.z) > r * r)
            continue;
        if (Math.abs(p.y - zone.y) > bodyHalfHeight(world) * 2)
            continue;
        const applied = applyDamage(world, p, zone.damage, zone.ownerId, zone.abilityId, events);
        // A trap that landed on an i-framed dasher still burns itself out — that is
        // what makes dashing through a glyph a real play rather than a free pass.
        if (applied > 0 || zone.damage <= 0) {
            applyAbilityEffects(world, zone.ownerId, p, zone.effects, events);
        }
        return false;
    }
    return true;
}
/**
 * Hex's Null Zone. The caster is exempt: a dome that suppressed and burned its
 * own owner would be strictly self-harming, and Hex already carries the lowest
 * damage in the roster. The asymmetry is stated rather than hidden.
 */
function stepAura(world, zone, events) {
    const shouldReport = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    const r = zone.radius + bodyRadius(world);
    // Direct hits scale `ability.damage` by the caster's damageMult at resolve,
    // and trap/barrage inherit that through the pre-multiplied `zone.damage`.
    // The aura burn reads `e.dps` straight off the authored effect — left raw it
    // was the one damage source that skipped Hex's 0.9, ticking 8 HP/s where her
    // multiplier says 7.2.
    const owner = findPlayer(world, zone.ownerId);
    const dmgMult = owner ? getCharacter(owner.characterId).damageMult : 1;
    for (const p of world.players) {
        if (p.phase !== "alive" || p.id === zone.ownerId)
            continue;
        if (casualProtected(world, p))
            continue;
        // The client draws Null Zone as a hemisphere of the ability's radius, so
        // the suppression volume is that same sphere. A flat XZ test made the dome
        // an infinite column: a Talon perched 25m over the floor circle was
        // silenced, dash-blocked and burned while visibly nowhere near it.
        if (dist2(p.x, p.y, p.z, zone.x, zone.y, zone.z) > r * r)
            continue;
        // Refreshed with a two-tick lifetime so walking out of the dome ends the
        // suppression immediately rather than a full duration later.
        for (const e of zone.effects) {
            if (e.kind === "suppress_abilities" || e.kind === "no_dash") {
                applyEffect(p, e.kind, ticksToMs(2), world.tick, zone.ownerId);
            }
            else if (e.kind === "damage_over_time") {
                applyDamage(world, p, e.dps * dmgMult * DT, zone.ownerId, zone.abilityId, events, shouldReport);
            }
        }
    }
}
/** Talon's Barrage: shells at seeded offsets inside the target area. */
function stepBarrage(world, zone, events) {
    const splash = BARRAGE_SHELL_SPLASH + bodyRadius(world);
    // Shell CENTRES stay a full splash radius inside the ring, so no shell's
    // blast escapes the drawn circle. Centres used to land anywhere in the ring
    // and splash 4m past it — a 16m kill zone under a 12m telegraph, killing
    // players who had dodged correctly. Standing on the drawn edge is still a
    // hit (rim centre + splash + body pad), so the whole ring stays dangerous.
    const spread = Math.max(0, zone.radius - BARRAGE_SHELL_SPLASH);
    while (zone.pulsesLeft > 0 && world.tick >= zone.nextPulseTick) {
        const offset = nextPointInDisc(world, spread);
        const sx = zone.x + offset.dx;
        const sz = zone.z + offset.dz;
        for (const p of world.players) {
            if (p.phase !== "alive" || p.id === zone.ownerId)
                continue;
            if (dist2XZ(p.x, p.z, sx, sz) > splash * splash)
                continue;
            // Same vertical band as every other splash. Without it a shell bursting
            // on the drawn floor hit a Talon perched 8m over the circle — or anyone
            // on a platform above or below it — at full damage.
            if (Math.abs(p.y - zone.y) > SPLASH_VERTICAL_REACH)
                continue;
            applyDamage(world, p, zone.damage, zone.ownerId, zone.abilityId, events);
        }
        destroyDecoysInSphere(world, sx, zone.y, sz, splash, events);
        damageCratesWhere(world, (v) => inSplash(v, sx, zone.y, sz, BARRAGE_SHELL_SPLASH), zone.damage, zone.ownerId, zone.abilityId, events);
        zone.pulsesLeft -= 1;
        zone.nextPulseTick += Math.max(1, zone.pulseIntervalTicks);
    }
    return zone.pulsesLeft > 0;
}
// ---------------------------------------------------------------------------
// Damage over time
// ---------------------------------------------------------------------------
export function applyDamageOverTime(world, events) {
    const shouldReport = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        for (const e of p.effects) {
            if (e.kind !== "damage_over_time")
                continue;
            applyDamage(world, p, e.magnitude * DT, e.sourceId, "dot", events, shouldReport);
        }
    }
}
// ---------------------------------------------------------------------------
// Decoys
// ---------------------------------------------------------------------------
/**
 * Autopilot's decoy: spawned ON the caster, running on along the caster's
 * heading — the direction they are moving, or facing if they are still — at
 * their pace, and never slower than a walk. It used to replay the caster's
 * last two seconds from the oldest point, appearing wherever they had been
 * two seconds before: a body popping in 4-5 m from anything, which is both a
 * teleport on screen and an instant tell.
 */
function spawnDecoy(world, caster, durationMs) {
    const speedXZ = Math.sqrt(caster.vx * caster.vx + caster.vz * caster.vz);
    let dirX;
    let dirZ;
    if (speedXZ > 1) {
        dirX = caster.vx / speedXZ;
        dirZ = caster.vz / speedXZ;
    }
    else {
        const fwd = forwardFromYaw(caster.yaw);
        dirX = fwd.x;
        dirZ = fwd.z;
    }
    const speed = Math.max(speedXZ, DECOY_MIN_SPEED * getCharacter(caster.characterId).speedMult);
    // On the ground under the caster, not at the caster's height: the decoy has
    // no gravity, and one cast mid-jump hung in the air for its whole life.
    const y = decoyRestY(world, caster.x, caster.y, caster.z) ?? caster.y;
    world.nextEntityId += 1;
    world.decoys.push({
        id: `${DECOY_ID_PREFIX}${world.nextEntityId}`,
        ownerId: caster.id,
        characterId: caster.characterId,
        x: caster.x,
        y,
        z: caster.z,
        yaw: caster.yaw,
        expiresTick: world.tick + msToTicks(durationMs),
        vx: dirX * speed,
        vz: dirZ * speed,
        rideX: 0,
        rideZ: 0,
        rideLeft: 0,
    });
}
/**
 * Advances every decoy one tick: a taxi ride if Robotaxi picked it, else its
 * run — the same wall sweep a player's body gets, and it parks at the rim
 * rather than walking into the void (a decoy falling to its death is a tell
 * as much as one standing in it).
 */
/**
 * The height a body standing at (x, z) rests at, looking down from `y`: the
 * highest solid top (or the arena floor) at or under it, or null over the
 * void. The decoy's whole idea of ground — it is born on it and runs along it.
 */
function decoyRestY(world, x, y, z) {
    const half = bodyHalfHeight(world);
    const reach = bodyRadius(world);
    let best = null;
    for (const plat of world.platforms) {
        if (!plat.solid)
            continue;
        if (Math.abs(x - plat.x) > plat.hx + reach || Math.abs(z - plat.z) > plat.hz + reach)
            continue;
        const rest = plat.y + plat.hy + half;
        if (rest <= y + DECOY_GROUND_EPS && (best === null || rest > best))
            best = rest;
    }
    const floor = world.map.floor;
    if (floor.kind === "solid" && onArenaFloor(world.map, x, z)) {
        const rest = floor.y + half;
        if (rest <= y + DECOY_GROUND_EPS && (best === null || rest > best))
            best = rest;
    }
    return best;
}
/** Slack when matching a decoy to the ground it stands on, in units. */
const DECOY_GROUND_EPS = 1e-6;
export function stepDecoys(world) {
    if (world.decoys.length === 0)
        return;
    const keep = [];
    for (const d of world.decoys) {
        if (world.tick >= d.expiresTick)
            continue;
        if (d.rideLeft > 0) {
            d.x += (d.rideX - d.x) / d.rideLeft;
            d.z += (d.rideZ - d.z) / d.rideLeft;
            d.rideLeft -= 1;
        }
        else if (d.vx !== 0 || d.vz !== 0) {
            const speed = Math.sqrt(d.vx * d.vx + d.vz * d.vz);
            const step = speed * DT;
            const swept = sweepPoint(d.x, d.y, d.z, d.vx / speed, d.vz / speed, step, world.platforms, undefined, chaosPlayerScale(world));
            // It parks at the rim, and at the edge of whatever it is running on:
            // with no gravity, a decoy that ran off a ledge walked on air.
            if (!onArenaFloor(world.map, swept.x, swept.z) || decoyRestY(world, swept.x, d.y, swept.z) !== d.y) {
                d.vx = 0;
                d.vz = 0;
            }
            else {
                d.x = swept.x;
                d.z = swept.z;
                if (swept.travelled < step) {
                    d.vx = 0;
                    d.vz = 0;
                }
            }
        }
        keep.push(d);
    }
    world.decoys = keep;
}
function destroyDecoy(world, decoyId, attackerId, abilityId, damage, events) {
    const i = world.decoys.findIndex((d) => d.id === decoyId);
    if (i < 0)
        return;
    const d = world.decoys[i];
    if (!d)
        return;
    // Reported with its nominal damage so the attacker's client shows a normal
    // hit. Nothing is credited: a decoy is not damage dealt and not ult charge.
    events.push({
        t: "hit",
        tick: world.tick,
        attackerId,
        targetId: d.id,
        abilityId,
        damage,
        x: d.x,
        y: d.y,
        z: d.z,
    });
    world.decoys.splice(i, 1);
}
function destroyDecoysInSphere(world, x, y, z, radius, events) {
    for (let i = world.decoys.length - 1; i >= 0; i--) {
        const d = world.decoys[i];
        if (!d)
            continue;
        if (dist2(d.x, d.y, d.z, x, y, z) > radius * radius)
            continue;
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId: "",
            targetId: d.id,
            abilityId: "",
            damage: 0,
            x: d.x,
            y: d.y,
            z: d.z,
        });
        world.decoys.splice(i, 1);
    }
}
function destroyDecoysOnSegment(world, ax, az, bx, bz, halfWidth, events) {
    for (let i = world.decoys.length - 1; i >= 0; i--) {
        const d = world.decoys[i];
        if (!d)
            continue;
        // Same rounded rectangle the live-target test uses: a decoy that survives
        // a hit its owner would have taken gives the disguise away.
        if (pointRectDist2XZ(d.x, d.z, ax, az, bx, bz, halfWidth) > bodyRadius(world) ** 2)
            continue;
        events.push({
            t: "hit",
            tick: world.tick,
            attackerId: "",
            targetId: d.id,
            abilityId: "",
            damage: 0,
            x: d.x,
            y: d.y,
            z: d.z,
        });
        world.decoys.splice(i, 1);
    }
}
export function creditFor(world, victim) {
    const aux = getAux(world, victim.id);
    const recentPlayerHit = aux.lastHitById !== null &&
        aux.lastHitById !== victim.id &&
        world.tick - aux.lastHitTick <= KILL_CREDIT_TICKS;
    if (recentPlayerHit && aux.lastHitById !== null) {
        return { killerId: aux.lastHitById, abilityId: aux.lastHitAbilityId, byZone: false };
    }
    if (world.tick - aux.lastZoneTick <= KILL_CREDIT_TICKS) {
        return { killerId: null, abilityId: "zone", byZone: true };
    }
    return { killerId: null, abilityId: "", byZone: false };
}
/** Cosine of an angle given in degrees, using the deterministic series. */
function cosDeg(deg) {
    return dcos((deg * PI) / 180);
}
/** Sine of an angle given in degrees, using the deterministic series. */
function sinDeg(deg) {
    return dsin((deg * PI) / 180);
}
//# sourceMappingURL=combat.js.map